import React from "react";
import Skeleton from "../ui/Skeleton";

export const AdsAnalyticsSkeleton: React.FC = () => {
  return (
    <div className="w-full flex flex-col gap-2 animate-pulse">
      {/* ========================================================================= */}
      {/* BANDA 1 SKELETON: Header de Controle & 4 KPIs                             */}
      {/* ========================================================================= */}
      <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs flex flex-col gap-3">
        {/* SectionHeader Skeleton */}
        <div className="flex items-start justify-between border-b pb-2 border-purple-100 mb-1">
          <div className="flex items-start gap-3">
            <Skeleton
              variant="rectangular"
              width={40}
              height={40}
              className="rounded-xl"
            />
            <div className="space-y-1.5">
              <Skeleton variant="text" width="140px" height="0.9rem" />
              <Skeleton
                variant="text"
                width="90px"
                height="0.75rem"
                className="opacity-50"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton
              variant="rectangular"
              width={200}
              height={32}
              className="rounded-lg"
            />
            <Skeleton
              variant="rectangular"
              width={120}
              height={32}
              className="rounded-lg hidden sm:block"
            />
          </div>
        </div>

        {/* 4 KPI Cards Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs flex flex-col justify-between"
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1 pr-2">
                  <Skeleton variant="text" width="60%" height="0.65rem" />
                  <Skeleton variant="text" width="80%" height="1.4rem" />
                </div>
                <Skeleton
                  variant="rectangular"
                  width={36}
                  height={36}
                  className="rounded-lg shrink-0"
                />
              </div>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={24}
                className="rounded-lg mt-2.5 opacity-60"
              />
            </div>
          ))}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* BANDA 2 SKELETON: Performance por Canal                                   */}
      {/* ========================================================================= */}
      <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs flex flex-col gap-3">
        {/* SectionHeader */}
        <div className="flex items-start gap-3 border-b pb-2 border-purple-100 mb-1">
          <Skeleton
            variant="rectangular"
            width={40}
            height={40}
            className="rounded-xl"
          />
          <div className="space-y-1.5 flex-1">
            <Skeleton variant="text" width="160px" height="0.9rem" />
            <Skeleton
              variant="text"
              width="110px"
              height="0.75rem"
              className="opacity-50"
            />
          </div>
        </div>

        {/* 4 Channel Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-white border border-purple-200 rounded-xl p-3 flex flex-col justify-between"
            >
              <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-purple-100">
                <div className="flex items-center gap-2">
                  <Skeleton
                    variant="rectangular"
                    width={32}
                    height={32}
                    className="rounded-lg"
                  />
                  <div className="space-y-1">
                    <Skeleton variant="text" width="80px" height="0.8rem" />
                    <Skeleton variant="text" width="50px" height="0.6rem" />
                  </div>
                </div>
                <Skeleton
                  variant="rectangular"
                  width={50}
                  height={20}
                  className="rounded-md"
                />
              </div>

              {/* 4 Sub-metrics grid */}
              <div className="grid grid-cols-2 gap-1.5">
                {[1, 2, 3, 4].map((j) => (
                  <div
                    key={j}
                    className="bg-purple-50/40 rounded-lg p-2 space-y-1"
                  >
                    <Skeleton variant="text" width="50%" height="0.55rem" />
                    <Skeleton variant="text" width="70%" height="0.9rem" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* BANDA 3 SKELETON: Ranking de Campanhas (+ Demografia LinkedIn, condicional) */}
      {/* ========================================================================= */}
      {/*
        O real (AdsAnalytics.tsx) só divide essa banda em 7/5 com o card de
        Demografia LinkedIn quando `linkedinCampaignsList.length > 0` — algo
        que só é conhecido DEPOIS da resposta da API, ou seja, o skeleton não
        tem como prever com certeza qual dos dois layouts vai aparecer.
        Para minimizar layout shift, assume-se aqui o caso mais comum e mais
        "seguro": sem LinkedIn Ads (grid-cols-1, Ranking ocupando a largura
        toda) — se a organização tiver campanhas LinkedIn, o card de
        Demografia simplesmente aparece ao lado ao carregar (shift por
        adição), o que é uma transição mais suave do que um card cheio de
        skeleton "sumir" quando não há LinkedIn (shift por remoção).
      */}
      <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs flex flex-col gap-2">
        <div className="flex items-start justify-between border-b pb-2 border-purple-100 mb-1">
          <div className="flex items-start gap-3">
            <Skeleton
              variant="rectangular"
              width={40}
              height={40}
              className="rounded-xl"
            />
            <div className="space-y-1.5">
              <Skeleton variant="text" width="150px" height="0.9rem" />
              <Skeleton
                variant="text"
                width="120px"
                height="0.75rem"
                className="opacity-50"
              />
            </div>
          </div>
          <Skeleton
            variant="rectangular"
            width={80}
            height={24}
            className="rounded-lg"
          />
        </div>

        <div className="divide-y divide-purple-100">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="flex items-center justify-between py-2.5 px-2"
            >
              <div className="flex items-center gap-2.5 flex-1 pr-3">
                <Skeleton
                  variant="rectangular"
                  width={28}
                  height={28}
                  className="rounded-lg shrink-0"
                />
                <div className="space-y-1 flex-1">
                  <Skeleton variant="text" width="60%" height="0.8rem" />
                  <Skeleton variant="text" width="30%" height="0.6rem" />
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <Skeleton
                  variant="text"
                  width={40}
                  height="0.8rem"
                  className="hidden sm:block"
                />
                <Skeleton
                  variant="text"
                  width={60}
                  height="0.8rem"
                  className="hidden sm:block"
                />
                <Skeleton
                  variant="rectangular"
                  width={48}
                  height={24}
                  className="rounded-lg"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdsAnalyticsSkeleton;
